/**
 * extensions/cornerstone/src/modules/suv/suvComputation.js
 *
 * SUV computation primitives — v2 with population-mean fallback support.
 *
 * Three layers:
 *   1. moToSuv()                       — pure SUV math
 *   2. getPetSuvFactors(petImageId)    — extract from DICOM metadata, with
 *                                        fallback to suvDefaults when needed
 *   3. (caller-level helpers handle ROI sampling — see computeRoiSuv.js)
 *
 * IMPORTANT: This implementation NEVER uses DoseCalibrationFactor (0054,1322)
 * as a dose surrogate. When dose is missing and fallback is disabled, returns
 * { ok: false, reason: '...' } so the UI can render '—'.
 */

import cornerstone from 'cornerstone-core';
import {
  HALF_LIVES_SEC,
  getSuvDefaults,
  inferTracer,
  getFallbackDecayCorrectedDoseBq,
} from './suvDefaults';

/**
 * Parse DICOM DA + TM into JS Date.
 *   DA = YYYYMMDD
 *   TM = HHMMSS[.frac]
 */
export function parseDicomDateTime(da, tm) {
  if (!da || !tm) return null;
  try {
    const y = Number(da.substr(0, 4));
    const m = Number(da.substr(4, 2));
    const d = Number(da.substr(6, 2));
    const tmClean = String(tm)
      .split('.')[0]
      .padStart(6, '0');
    const hh = Number(tmClean.substr(0, 2));
    const mm = Number(tmClean.substr(2, 2));
    const ss = Number(tmClean.substr(4, 2));
    return new Date(Date.UTC(y, m - 1, d, hh, mm, ss));
  } catch (e) {
    return null;
  }
}

/**
 * Pure SUV formula.
 *   SUV = mo × weight_g / decayCorrectedDose_Bq
 *       = mo × weight_kg × 1000 / decayCorrectedDose_Bq
 */
export function moToSuv(mo, decayCorrectedDoseBq, patientWeightKg) {
  if (!isFinite(mo) || mo < 0) return null;
  if (!isFinite(decayCorrectedDoseBq) || decayCorrectedDoseBq <= 0) return null;
  if (!isFinite(patientWeightKg) || patientWeightKg <= 0) return null;
  return (mo * patientWeightKg * 1000) / decayCorrectedDoseBq;
}

/**
 * Resolve SUV factors for a PET imageId. Returns:
 *   { ok: true,
 *     decayCorrectedDoseBq, patientWeightKg, halfLifeSec, tracer,
 *     dataSource: { dose, weight, uptake, halfLife },   // 'dicom' | 'fallback'
 *   }
 * Or { ok: false, reason } if computation impossible (e.g. wrong Units, or
 * required field missing AND fallback disabled).
 */
export function getPetSuvFactors(petImageId) {
  if (!petImageId) return { ok: false, reason: 'no-image-id' };

  const defaults = getSuvDefaults();

  /* Try multiple module names — different cornerstone metadata providers
   * expose these tags under different keys. */
  const patient =
    cornerstone.metaData.get('patientStudyModule', petImageId) ||
    cornerstone.metaData.get('patientModule', petImageId) ||
    {};
  const pet =
    cornerstone.metaData.get('petIsotopeModule', petImageId) ||
    cornerstone.metaData.get('petSeriesModule', petImageId) ||
    {};
  const series =
    cornerstone.metaData.get('generalSeriesModule', petImageId) || {};
  const instance = cornerstone.metaData.get('instance', petImageId) || {};

  /* ----- Units check ----- */
  const units = pet.units || instance.Units || instance['00541001'] || 'BQML';
  if (units !== 'BQML') {
    return { ok: false, reason: 'units-not-bqml', units };
  }

  /* ----- Patient weight ----- */
  let patientWeightKg =
    patient.patientWeight ||
    patient.PatientWeight ||
    instance.PatientWeight ||
    instance['00101030'] ||
    null;
  let weightFromFallback = false;
  if (!patientWeightKg) {
    if (!defaults.enableFallback) {
      return { ok: false, reason: 'missing-patient-weight' };
    }
    patientWeightKg = defaults.defaultPatientWeightKg;
    weightFromFallback = true;
  }

  /* ----- Radiopharmaceutical sequence (may be missing under anonymization) ----- */
  const rpis =
    pet.radiopharmaceuticalInfo ||
    instance.RadiopharmaceuticalInformationSequence ||
    instance['00540016'] ||
    null;
  const rpItem = Array.isArray(rpis) ? rpis[0] : rpis;

  const radionuclideTotalDoseBq =
    rpItem?.radionuclideTotalDose ||
    rpItem?.RadionuclideTotalDose ||
    rpItem?.['00181074'] ||
    null;

  let halfLifeSec =
    rpItem?.radionuclideHalfLife ||
    rpItem?.RadionuclideHalfLife ||
    rpItem?.['00181075'] ||
    null;

  /* Tracer detection — used for fallback half-life and dose lookup */
  const tracerHint =
    rpItem?.radionuclideCodeSequence?.[0]?.codeMeaning ||
    rpItem?.RadionuclideCodeSequence?.[0]?.CodeMeaning ||
    series.seriesDescription ||
    series.SeriesDescription ||
    '';
  const tracer = inferTracer(tracerHint);

  if (!halfLifeSec) {
    halfLifeSec = HALF_LIVES_SEC[tracer] || HALF_LIVES_SEC['F-18'];
  }

  /* ----- Compute decay-corrected dose ----- */
  let decayCorrectedDoseBq;
  let doseFromFallback = false;

  if (radionuclideTotalDoseBq) {
    /* Standard path — DICOM has dose. Apply decay correction. */
    const rpStartDateTime =
      rpItem?.radiopharmaceuticalStartDateTime ||
      rpItem?.RadiopharmaceuticalStartDateTime ||
      rpItem?.['00181078'] ||
      null;
    const rpStartTime =
      rpItem?.radiopharmaceuticalStartTime ||
      rpItem?.RadiopharmaceuticalStartTime ||
      rpItem?.['00181072'] ||
      null;
    const studyDate =
      series.studyDate ||
      series.StudyDate ||
      instance.StudyDate ||
      instance['00080020'];

    let injectionDateTime = null;
    if (rpStartDateTime) {
      const dt = String(rpStartDateTime)
        .split('.')[0]
        .padStart(14, '0');
      injectionDateTime = parseDicomDateTime(dt.substr(0, 8), dt.substr(8, 6));
    } else if (rpStartTime && studyDate) {
      injectionDateTime = parseDicomDateTime(studyDate, rpStartTime);
    }

    const seriesDate =
      series.seriesDate || series.SeriesDate || instance.SeriesDate;
    const seriesTime =
      series.seriesTime || series.SeriesTime || instance.SeriesTime;
    const seriesDateTime = parseDicomDateTime(seriesDate, seriesTime);

    const decayCorrection =
      pet.decayCorrection ||
      instance.DecayCorrection ||
      instance['00541102'] ||
      'START';

    if (decayCorrection === 'ADMIN' || !injectionDateTime || !seriesDateTime) {
      decayCorrectedDoseBq = radionuclideTotalDoseBq;
    } else if (decayCorrection === 'START') {
      const elapsedSec =
        (seriesDateTime.getTime() - injectionDateTime.getTime()) / 1000;
      if (elapsedSec < 0 || elapsedSec > 24 * 3600) {
        /* Implausible elapsed time. Fall through to fallback if enabled. */
        if (defaults.enableFallback) {
          decayCorrectedDoseBq = getFallbackDecayCorrectedDoseBq(tracer);
          doseFromFallback = true;
        } else {
          return { ok: false, reason: 'implausible-elapsed-time', elapsedSec };
        }
      } else {
        const decayFactor = Math.exp((-Math.log(2) * elapsedSec) / halfLifeSec);
        decayCorrectedDoseBq = radionuclideTotalDoseBq * decayFactor;
      }
    } else if (decayCorrection === 'NONE') {
      if (defaults.enableFallback) {
        decayCorrectedDoseBq = getFallbackDecayCorrectedDoseBq(tracer);
        doseFromFallback = true;
      } else {
        return { ok: false, reason: 'decay-correction-none' };
      }
    } else {
      decayCorrectedDoseBq = radionuclideTotalDoseBq;
    }
  } else {
    /* Dose missing — anonymized dataset case. Use population mean. */
    if (!defaults.enableFallback) {
      return { ok: false, reason: 'missing-injected-dose' };
    }
    decayCorrectedDoseBq = getFallbackDecayCorrectedDoseBq(tracer);
    doseFromFallback = true;
  }

  return {
    ok: true,
    decayCorrectedDoseBq,
    patientWeightKg,
    halfLifeSec,
    tracer,
    units,
    dataSource: {
      dose: doseFromFallback ? 'fallback' : 'dicom',
      weight: weightFromFallback ? 'fallback' : 'dicom',
    },
  };
}
